import { useEffect, useMemo, useRef, useState } from 'react'
import type { MarketTick, RoundState } from '../shared/contracts'
import { buildSmoothPath } from './smoothPath'

interface ClassicCurrentOverlayProps {
  tick?: MarketTick
  history: MarketTick[]
  round: RoundState
  priceCamera: number
  reducedMotion: boolean
  frenzy: boolean
}

interface OverlaySize {
  width: number
  height: number
  playheadRatio: number
  rowPitch: number
}

const HISTORY_WINDOW_MS = 60_000

export function ClassicCurrentOverlay({ tick, history, round, priceCamera, reducedMotion, frenzy }: ClassicCurrentOverlayProps) {
  const host = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<OverlaySize>({ width: 1, height: 1, playheadRatio: 0.36, rowPitch: 37 })

  useEffect(() => {
    const element = host.current
    if (!element) return

    const measure = () => {
      const bounds = element.getBoundingClientRect()
      const styles = getComputedStyle(element)
      const playhead = Number.parseFloat(styles.getPropertyValue('--classic-playhead'))
      const rowPitch = Number.parseFloat(styles.getPropertyValue('--classic-row-pitch'))
      setSize({
        width: Math.max(1, bounds.width),
        height: Math.max(1, bounds.height),
        playheadRatio: Number.isFinite(playhead) ? playhead / 100 : 0.36,
        rowPitch: Number.isFinite(rowPitch) ? rowPitch : 37,
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const ticks = useMemo(() => {
    const merged = new Map(history.map((item) => [item.sequence, item]))
    if (tick) merged.set(tick.sequence, tick)
    return [...merged.values()].sort((left, right) => left.sourceTimestamp - right.sourceTimestamp)
  }, [history, tick])

  const latestTimestamp = tick?.sourceTimestamp ?? ticks.at(-1)?.sourceTimestamp ?? round.startedAt
  const visibleTicks = ticks.filter((item) => item.sourceTimestamp >= latestTimestamp - HISTORY_WINDOW_MS)
  const priceStep = round.classicRules?.priceStep ?? round.bandSize
  const playheadX = size.width * size.playheadRatio
  const pointFor = (item: MarketTick) => ({
    x: playheadX - ((latestTimestamp - item.sourceTimestamp) / HISTORY_WINDOW_MS) * playheadX,
    y: size.height / 2 - ((item.price - priceCamera) / priceStep) * size.rowPitch,
  })
  const points = visibleTicks.map(pointFor)
  if (points.length === 0) points.push({ x: playheadX, y: size.height / 2 })
  if (points.length === 1) points.unshift({ x: Math.max(0, playheadX - 18), y: points[0].y })
  const path = buildSmoothPath(points)
  const last = points.at(-1)!

  return (
    <div ref={host} className={`classic-current-overlay ${reducedMotion ? 'reduced' : ''}`} aria-hidden="true">
      <svg viewBox={`0 0 ${size.width} ${size.height}`} preserveAspectRatio="none">
        <path className={`classic-current-line ${frenzy ? 'frenzy' : ''}`} d={path} />
      </svg>
      <img
        className="classic-current-rider"
        src="/assets/current-rider-fish.png"
        alt=""
        style={{ left: `${last.x}px`, top: `${last.y}px` }}
      />
    </div>
  )
}
